import { useCallback, useEffect, useMemo, useState } from 'react';
import { UserApi } from '../service';
import { canManageUserAccounts } from '../constants/roles';
import {
  collectDeletedEmailsFromSharePools,
  filterShareSuggestionEmails,
  mergeDeletedEmailSets,
} from '../utils/userPresence';

type SharePool = readonly unknown[] | null | undefined;

function sharePoolsCacheKey(sharePools: SharePool[]): string {
  return sharePools
    .map((pool) =>
      (pool ?? [])
        .map((raw) => {
          const u = raw as { email?: string | null; isDeleted?: boolean };
          const email = String(u.email ?? '').trim().toLowerCase();
          return `${email}:${u.isDeleted === true ? 1 : 0}`;
        })
        .join(',')
    )
    .join(';');
}

export function useShareSuggestionDeletedEmails(
  active: boolean,
  userApi: UserApi,
  userRole: string | undefined,
  sharePools: SharePool[]
) {
  const [apiDeletedEmails, setApiDeletedEmails] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    if (!active) {
      setApiDeletedEmails(new Set());
      return;
    }

    if (!canManageUserAccounts(userRole)) {
      setApiDeletedEmails(new Set());
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const response = await userApi.apiUserGet({ credentials: 'include' });
        if (cancelled) return;
        const next = new Set<string>();
        for (const user of response.data?.users ?? []) {
          if (user.isDeleted && user.email) {
            next.add(String(user.email).trim().toLowerCase());
          }
        }
        setApiDeletedEmails(next);
      } catch {
        if (!cancelled) setApiDeletedEmails(new Set());
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [active, userApi, userRole]);

  const sharePoolsKey = useMemo(() => sharePoolsCacheKey(sharePools), [sharePools]);

  const poolDeletedEmails = useMemo(
    () => collectDeletedEmailsFromSharePools(...sharePools),
    // sharePoolsKey: aynı içerikte yeni dizi referansı döngüyü tetiklemesin
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sharePoolsKey]
  );

  const apiDeletedKey = useMemo(
    () => Array.from(apiDeletedEmails).sort().join('\0'),
    [apiDeletedEmails]
  );

  const deletedEmailSet = useMemo(
    () => mergeDeletedEmailSets(poolDeletedEmails, apiDeletedEmails),
    [poolDeletedEmails, apiDeletedKey]
  );

  const filterSuggestionEmails = useCallback(
    (emails: string[]) => filterShareSuggestionEmails(emails, deletedEmailSet),
    [deletedEmailSet]
  );

  return { deletedEmailSet, filterSuggestionEmails };
}
